/**
 * A simple logger utility that can be toggled based on environment.
 * In production, log messages will be suppressed, while error messages will always be shown.
 * 
 * Logging levels:
 * - log: Debug information (development only)
 * - info: Informational messages (development only)
 * - warn: Warning messages (configurable based on environment)
 * - error: Error messages (always shown in all environments)
 */

// Check environment variables
const isProduction = import.meta.env.VITE_APP_ENV === 'production'

// Allow configuring whether warnings should be shown in production via environment variable
// If VITE_SHOW_WARNINGS_IN_PROD is not specified, default to hiding warnings in production
const showWarningsInProduction = import.meta.env.VITE_SHOW_WARNINGS_IN_PROD === 'true'

/**
 * Logger utility with environment-aware methods
 */
export const logger = {
  /**
   * Log a message to the console (suppressed in production)
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  log(message: any, ...args: any[]): void {
    if (!isProduction) {
      console.log(message, ...args)
    }
  },

  /**
   * Log an info message to the console (suppressed in production)
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  info(message: any, ...args: any[]): void {
    if (!isProduction) {
      console.info(message, ...args)
    }
  },

  /**
   * Log a warning message to the console (configurable for production)
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  warn(message: any, ...args: any[]): void {
    if (!isProduction || showWarningsInProduction) {
      console.warn(message, ...args)
    }
  },

  /**
   * Log an error message to the console (always shown in all environments)
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  error(message: any, ...args: any[]): void {
    // Always log errors regardless of environment
    console.error(message, ...args)
    
    // Optionally, this could be a place to add error reporting to a service like Sentry
    // if (typeof window !== 'undefined' && window.Sentry) {
    //   window.Sentry.captureException(message instanceof Error ? message : new Error(String(message)))
    // }
  },

  /**
   * Log a group to the console (suppressed in production)
   * @param label - The group label
   */
  group(label: string): void {
    if (!isProduction) {
      console.group(label)
    }
  },

  /**
   * End a log group (suppressed in production)
   */
  groupEnd(): void {
    if (!isProduction) {
      console.groupEnd()
    }
  },

  /**
   * Log a table to the console (suppressed in production)
   * @param data - The data to display in the table
   */
  table(data: any): void {
    if (!isProduction) {
      console.table(data)
    }
  },

  /**
   * Track performance with console.time (suppressed in production)
   * @param label - The timer label
   */
  time(label: string): void {
    if (!isProduction) {
      console.time(label)
    }
  },

  /**
   * End a performance timer (suppressed in production)
   * @param label - The timer label
   */
  timeEnd(label: string): void {
    if (!isProduction) {
      console.timeEnd(label)
    }
  },
  
  /**
   * Force log a message even in production
   * Use this only for critical information that must be logged in all environments
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  forceLog(message: any, ...args: any[]): void {
    console.log(message, ...args)
  }
}

/**
 * Create a namespaced logger
 * @param namespace - The namespace for this logger
 * @returns A logger with the namespace prefix
 */
export function createLogger(namespace: string) {
  return {
    log(message: any, ...args: any[]): void {
      logger.log(`[${namespace}] ${message}`, ...args)
    },
    info(message: any, ...args: any[]): void {
      logger.info(`[${namespace}] ${message}`, ...args)
    },
    warn(message: any, ...args: any[]): void {
      logger.warn(`[${namespace}] ${message}`, ...args)
    },
    error(message: any, ...args: any[]): void {
      logger.error(`[${namespace}] ${message}`, ...args)
    },
    group(label: string): void {
      logger.group(`[${namespace}] ${label}`)
    },
    groupEnd(): void {
      logger.groupEnd()
    },
    table(data: any): void {
      logger.table(data)
    },
    time(label: string): void {
      logger.time(`[${namespace}] ${label}`)
    },
    timeEnd(label: string): void {
      logger.timeEnd(`[${namespace}] ${label}`)
    },
    forceLog(message: any, ...args: any[]): void {
      logger.forceLog(`[${namespace}] ${message}`, ...args)
    }
  }
}

export default logger 